import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { catchError, map, mergeMap, tap, timeout } from 'rxjs/operators';
import { readOrchestratorBaseUrl } from '@root/src/environments/orchestrator-base-url';
import {
  ChatAttachmentUpload,
  ChatHttpResponse,
  ChatJobArtifact,
  DataConstraints,
  GuideHttpResponse
} from './models/orchestrator-chat.models';

@Injectable({ providedIn: 'root' })
export class OrchestratorChatService {
  private readonly baseUrl = readOrchestratorBaseUrl();

  constructor(private readonly http: HttpClient) {}

  startGuide(sessionId: string | null): Observable<GuideHttpResponse> {
    return this.http.post<GuideHttpResponse>(`${this.baseUrl}/guide/start`, { session_id: sessionId });
  }

  guideCallback(sessionId: string, callback: string, messageId: string | null): Observable<GuideHttpResponse> {
    return this.http.post<GuideHttpResponse>(`${this.baseUrl}/guide/callback`, {
      session_id: sessionId,
      callback,
      message_id: messageId
    });
  }

  uploadPhoto(file: File): Observable<ChatAttachmentUpload> {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.http.post<ChatAttachmentUpload>(`${this.baseUrl}/chat/attachments`, body).pipe(
      timeout(120000),
      catchError((err: HttpErrorResponse) => throwError(() => err))
    );
  }

  sendMessage(
    message: string,
    sessionId: string | null,
    locale: string,
    attachments?: string[]
  ): Observable<ChatHttpResponse> {
    return this.http
      .post<ChatHttpResponse>(`${this.baseUrl}/chat`, {
        message,
        session_id: sessionId,
        locale,
        ...(attachments?.length ? { attachments } : {})
      })
      .pipe(
        timeout(310000),
        catchError((err: HttpErrorResponse) => throwError(() => err))
      );
  }

  callTool<T>(tool: string, args: Record<string, unknown> = {}): Observable<T | null> {
    return this.http
      .post<{ mcp?: { data?: T } }>(`${this.baseUrl}/tools/call`, { tool, arguments: args })
      .pipe(map((res) => res?.mcp?.data ?? null));
  }

  getDataConstraints(): Observable<DataConstraints> {
    return this.http.get<{ mcp?: { data?: DataConstraints } }>(`${this.baseUrl}/tools/get_data_constraints`).pipe(
      map((res) => res?.mcp?.data ?? {})
    );
  }

  getJobStatus(jobId: string): Observable<ChatJobArtifact | null> {
    return this.http
      .post<{ mcp?: { data?: ChatJobArtifact } }>(`${this.baseUrl}/tools/get_job_status`, { job_id: jobId })
      .pipe(map((res) => res?.mcp?.data ?? null));
  }

  downloadReport(reportId: string, fileName?: string | null): Observable<void> {
    return this.downloadUrl(`${this.baseUrl}/reports/${encodeURIComponent(reportId)}`, fileName);
  }

  downloadUrl(url: string, fileName?: string | null): Observable<void> {
    const target = url.startsWith('http') ? this.toSameOriginIfPossible(url) : url;
    return this.http.get(target, { responseType: 'blob', observe: 'response' }).pipe(
      tap((res) => this.saveBlob(res, fileName)),
      map(() => undefined),
      catchError((err: HttpErrorResponse) => this.rethrowBlobError(err))
    );
  }

  loadFilePreview(url: string): Observable<string> {
    const target = url.startsWith('http') ? this.toSameOriginIfPossible(url) : url;
    return this.http.get(target, { responseType: 'blob' }).pipe(
      map((blob) => URL.createObjectURL(blob))
    );
  }

  private saveBlob(res: HttpResponse<Blob>, fileName?: string | null): void {
    const blob = res.body;
    if (!blob || blob.size === 0) {
      throw new HttpErrorResponse({
        error: { error: 'Empty file response.' },
        status: res.status,
        statusText: res.statusText,
        url: res.url ?? undefined
      });
    }
    const name = this.safeFileName(
      fileName || this.fileNameFromDisposition(res.headers.get('content-disposition')) || 'report'
    );
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = name;
    link.rel = 'noopener';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 2000);
  }

  private rethrowBlobError(err: HttpErrorResponse): Observable<never> {
    if (!(err.error instanceof Blob)) {
      return throwError(() => err);
    }
    return from(err.error.text()).pipe(
      mergeMap((text) => {
        let parsed: unknown = text;
        try {
          parsed = JSON.parse(text);
        } catch {
          parsed = { error: text || err.message };
        }
        return throwError(
          () =>
            new HttpErrorResponse({
              error: parsed,
              headers: err.headers,
              status: err.status,
              statusText: err.statusText,
              url: err.url ?? undefined
            })
        );
      })
    );
  }

  private safeFileName(name: string): string {
    return name.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'report';
  }

  private toSameOriginIfPossible(url: string): string {
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.pathname.startsWith('/api/orchestrator')) {
        return parsed.pathname + parsed.search;
      }
    } catch {
      return url;
    }
    return url;
  }

  private fileNameFromDisposition(header: string | null): string | null {
    if (!header) {
      return null;
    }
    const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(header);
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  }
}
